/**
 * OrganizationContracts - Gestione Contratti Organization
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: Pagina per visualizzare i contratti dell'organizzazione
 * - Documenti da firmare (pending)
 * - Lista contratti firmati
 * - Download PDF contratti
 * 
 * FIX: Aggiunta sezione documenti pendenti da firmare
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationApi, organizationOnboardingApi, legalDocumentsApi } from '../../services/organizationApi'
import { Card, PageLoader, Badge, EmptyState } from '../../components/ui'

import {
  FileText,
  ArrowLeft,
  Download,
  Eye,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Building2,
  Filter,
  FileSignature,
  ExternalLink,
  Loader2,
  Check,
  ChevronUp,
  ChevronDown,
  History
} from 'lucide-react'

// ============================================
// DOCUMENT TYPE DISPLAY NAMES
// ============================================

const TYPE_DISPLAY_NAMES = {
  tos: 'Termini di Servizio',
  terms: 'Termini di Servizio',
  privacy: 'Informativa Privacy',
  dpa: 'Data Processing Agreement',
  cookie: 'Cookie Policy',
  conduct_code: 'Codice di Condotta',
  liability_waiver: 'Liberatoria Responsabilità',
  insurance: 'Polizza Assicurativa',
  parental_consent_template: 'Modello Consenso Genitori',
  other: 'Altro'
}

// ============================================
// CONTRACT STATUS BADGE
// ============================================

function ContractStatusBadge({ status }) {
  const config = {
    active: { label: 'Firmato', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
    pending: { label: 'Da Firmare', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
    expired: { label: 'Scaduto', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400', icon: AlertCircle },
    cancelled: { label: 'Annullato', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle }
  }
  
  const { label, color, icon: Icon } = config[status] || config.pending
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${color}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  )
}

// ============================================
// PENDING DOCUMENT CARD
// Con scroll tracking per lettura obbligatoria
// ============================================

function PendingDocumentCard({ document, onAccept, accepting }) {
  const [showContent, setShowContent] = useState(false)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const contentRef = useRef(null)
  
  const typeDisplay = TYPE_DISPLAY_NAMES[document.type] || document.type
  const hasInlineContent = !!document.content?.body
  
  // Se requiresFullRead è true, deve scrollare fino in fondo
  // OPPURE visitare link esterno/PDF
  const requiresFullRead = document.requiresFullRead
  const canAccept = !requiresFullRead || hasScrolledToBottom
  
  // Handler per tracciare lo scroll
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    const progress = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
    setScrollProgress(Math.min(progress, 100))
    
    // Considera letto se ha scrollato almeno al 90%
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true)
    }
  }, [])
  
  // Se clicca su link esterno o PDF, considera letto
  const handleExternalClick = () => {
    setHasScrolledToBottom(true)
  }
  
  // Quando apre il contenuto, verifica se richiede scroll
  useEffect(() => {
    if (showContent && contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current
      // Se il contenuto non richiede scroll, considera già letto
      if (scrollHeight <= clientHeight) {
        setHasScrolledToBottom(true)
        setScrollProgress(100)
      }
    }
  }, [showContent])
  
  const handleAccept = () => {
    if (requiresFullRead && !canAccept) {
      return
    }
    onAccept(document)
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-yellow-300 dark:border-yellow-700 overflow-hidden">
      {/* Header */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
            <FileSignature className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {document.title || typeDisplay}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Versione {document.version} • {typeDisplay}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {requiresFullRead && (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              hasScrolledToBottom 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
            }`}>
              {hasScrolledToBottom ? '✓ Letto' : 'Lettura obbligatoria'}
            </span>
          )}
          <span className="px-3 py-1 text-xs font-medium bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full">
            Da firmare
          </span>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Summary */}
        {document.content?.summary && (
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {document.content.summary}
          </p>
        )}
        
        {/* Deadline warning */}
        {document.signatureDeadline && (
          <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
            <AlertTriangle className="h-4 w-4" />
            <span>
              Scadenza: {new Date(document.signatureDeadline).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
        )}
        
        {/* View content buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {document.content?.externalUrl && (
            <a
              href={document.content.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
              onClick={handleExternalClick}
            >
              <ExternalLink className="h-4 w-4" />
              Apri documento
            </a>
          )}
          
          {document.content?.body && (
            <button
              onClick={() => setShowContent(!showContent)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <Eye className="h-4 w-4" />
              {showContent ? 'Nascondi contenuto' : 'Leggi contenuto'}
              {showContent ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
          
          {document.pdf?.fileId && (
            <a
              href={`/api/legal/pdf/${document.pdf.fileId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={handleExternalClick}
            >
              <Download className="h-4 w-4" />
              Scarica PDF
            </a>
          )}
          
          {/* Progress indicator */}
          {showContent && hasInlineContent && requiresFullRead && (
            <div className="flex items-center gap-2 ml-auto text-sm">
              <span className={hasScrolledToBottom ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>
                {hasScrolledToBottom ? '✓ Completato' : `${scrollProgress}%`}
              </span>
              {!hasScrolledToBottom && (
                <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${scrollProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Expandable content with scroll tracking */}
        {showContent && document.content?.body && (
          <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div 
              ref={contentRef}
              onScroll={handleScroll}
              className="p-4 bg-gray-50 dark:bg-gray-900 max-h-64 overflow-y-auto"
            >
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {document.content.format === 'html' ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(document.content.body) }} />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans bg-transparent p-0 m-0">
                    {document.content.body}
                  </pre>
                )}
              </div>
              
              {/* Scroll hint at bottom */}
              {requiresFullRead && !hasScrolledToBottom && (
                <div className="mt-4 pt-4 border-t border-dashed border-gray-300 dark:border-gray-600 text-center">
                  <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center justify-center gap-2">
                    <ChevronDown className="h-4 w-4 animate-bounce" />
                    Scorri fino in fondo per abilitare l'accettazione
                    <ChevronDown className="h-4 w-4 animate-bounce" />
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Accept section */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          {requiresFullRead && !canAccept && (
            <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {showContent 
                    ? 'Scorri fino in fondo al documento per poter accettare' 
                    : 'Apri e leggi il documento completo prima di accettarlo'}
                </span>
              </p>
            </div>
          )}
          
          {canAccept && requiresFullRead && (
            <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>Documento letto. Ora puoi accettare i termini.</span>
              </p>
            </div>
          )}
          
          <button
            onClick={handleAccept}
            disabled={accepting || !canAccept}
            className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 font-medium rounded-lg transition-colors ${
              accepting || !canAccept
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            {accepting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Accettazione in corso...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Accetto i termini del documento
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// SIGNED CONTRACT ROW
// Con contenuto espandibile e card cliccabile
// ============================================

function SignedContractRow({ contract, documentDetails }) {
  const [expanded, setExpanded] = useState(false)
  const typeDisplay = TYPE_DISPLAY_NAMES[contract.documentType] || contract.documentType
  
  // Se abbiamo i dettagli del documento, usiamo quelli
  const doc = documentDetails || {}
  const hasContent = doc.content?.body || doc.content?.externalUrl || doc.pdf?.fileId
  
  const handleToggle = () => {
    if (hasContent) {
      setExpanded(!expanded)
    }
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header - Cliccabile su desktop, con icona su mobile */}
      <div 
        onClick={handleToggle}
        className={`flex items-center justify-between p-4 ${
          hasContent 
            ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group' 
            : ''
        }`}
      >
        <div className="flex items-center gap-4">
          <div className={`p-2 bg-green-100 dark:bg-green-900/30 rounded-lg ${
            hasContent ? 'group-hover:bg-green-200 dark:group-hover:bg-green-800/50 transition-colors' : ''
          }`}>
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {doc.title || typeDisplay}
            </p>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span>Versione {contract.documentVersion}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Firmato il {new Date(contract.acceptedAt).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <ContractStatusBadge status="active" />
          {hasContent && (
            <div className={`p-2 text-gray-400 rounded-lg transition-all ${
              expanded 
                ? 'text-primary-600 dark:text-primary-400' 
                : 'md:opacity-0 md:group-hover:opacity-100 text-primary-500'
            }`}>
              {expanded ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded content */}
      {expanded && hasContent && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          {/* Avviso versione diversa */}
          {doc._versionMismatch && (
            <div className="mx-3 mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Versione aggiornata
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Hai firmato la versione {doc._signedVersion}, ma il documento è stato aggiornato alla versione {doc._currentVersion}. 
                    Stai visualizzando la versione corrente.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
            {doc.content?.externalUrl && (
              <a
                href={doc.content.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Apri link esterno
              </a>
            )}
            {doc.pdf?.fileId && (
              <a
                href={doc.pdf.url || `/api/legal/pdf/${doc.pdf.fileId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Scarica PDF
              </a>
            )}
          </div>
          
          {/* Document body */}
          {doc.content?.body && (
            <div className="p-4 max-h-80 overflow-y-auto">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {doc.content.format === 'html' ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.content.body) }} />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans bg-transparent p-0 m-0">
                    {doc.content.body}
                  </pre>
                )}
              </div>
            </div>
          )}
          
          {/* No body but has external link or PDF */}
          {!doc.content?.body && (doc.content?.externalUrl || doc.pdf?.fileId) && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <FileSignature className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {doc.pdf?.fileId 
                  ? 'Scarica il PDF per visualizzare il documento completo'
                  : 'Clicca sul link esterno per visualizzare il documento completo'
                }
              </p>
            </div>
          )}
          
          {/* Documento eliminato */}
          {doc._documentDeleted && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                Questo documento non è più disponibile per la visualizzazione.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// HISTORY CONTRACT ROW
// Per documenti dello storico (sostituiti/archiviati)
// ============================================

function HistoryContractRow({ history, documentDetails }) {
  const [expanded, setExpanded] = useState(false)
  const typeDisplay = TYPE_DISPLAY_NAMES[history.documentType] || history.documentType
  
  // Se abbiamo i dettagli del documento, usiamo quelli
  const doc = documentDetails || {}
  const hasContent = doc.content?.body || doc.content?.externalUrl || doc.pdf?.fileId
  
  const handleToggle = () => {
    if (hasContent) {
      setExpanded(!expanded)
    }
  }
  
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      {/* Header - Cliccabile */}
      <div 
        onClick={handleToggle}
        className={`p-4 flex items-center justify-between ${
          hasContent 
            ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group' 
            : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-gray-100 dark:bg-gray-700 rounded-lg ${
            hasContent ? 'group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors' : ''
          }`}>
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {history.documentTitle || doc.title || typeDisplay}
            </p>
            <p className="text-sm text-gray-500">
              v{history.documentVersion} • Firmato il {new Date(history.acceptedAt).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            {history.supersededAt ? (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                <AlertCircle className="h-3 w-3" />
                Sostituito
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                Archiviato
              </span>
            )}
            {history.supersededReason && (
              <p className="text-xs text-gray-400 mt-1">
                {history.supersededReason === 'admin_request' && 'Rifirma richiesta'}
                {history.supersededReason === 'version_update' && 'Nuova versione'}
                {history.supersededReason === 'policy_change' && 'Policy aggiornata'}
              </p>
            )}
          </div>
          {hasContent && (
            <div className={`p-1.5 text-gray-400 rounded-lg transition-all ${
              expanded 
                ? 'text-primary-600 dark:text-primary-400' 
                : 'md:opacity-0 md:group-hover:opacity-100 text-gray-500'
            }`}>
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded content */}
      {expanded && hasContent && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 p-3 border-b border-gray-100 dark:border-gray-700">
            {doc.content?.externalUrl && (
              <a
                href={doc.content.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Apri link esterno
              </a>
            )}
            {doc.pdf?.fileId && (
              <a
                href={doc.pdf.url || `/api/legal/pdf/${doc.pdf.fileId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Scarica PDF
              </a>
            )}
          </div>
          
          {/* Document body */}
          {doc.content?.body && (
            <div className="p-4 max-h-60 overflow-y-auto">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {doc.content.format === 'html' ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.content.body) }} />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans bg-transparent p-0 m-0">
                    {doc.content.body}
                  </pre>
                )}
              </div>
            </div>
          )}
          
          {/* No body but has external link or PDF */}
          {!doc.content?.body && (doc.content?.externalUrl || doc.pdf?.fileId) && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <FileSignature className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {doc.pdf?.fileId 
                  ? 'Scarica il PDF per visualizzare il documento completo'
                  : 'Clicca sul link esterno per visualizzare il documento completo'
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function OrganizationContracts() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pendingDocs, setPendingDocs] = useState([])
  const [signedContracts, setSignedContracts] = useState([])
  const [contractsHistory, setContractsHistory] = useState([])
  const [documentDetails, setDocumentDetails] = useState({}) // Mappa documentId -> dettagli
  const [accepting, setAccepting] = useState(null)
  
  const { showSuccess, showError } = useNotification()
  
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Usa il nuovo endpoint che ritorna tutto insieme
      const response = await organizationOnboardingApi.getContractsFull().catch(err => {
        console.error('Error loading contracts-full:', err)
        // Fallback ai vecchi endpoint se il nuovo non esiste
        return null
      })
      
      if (response?.data?.success) {
        // Nuovo endpoint disponibile
        const data = response.data.data
        setPendingDocs(data.pending || [])
        setSignedContracts(data.contracts || [])
        setContractsHistory(data.contractsHistory || [])
        
        // Carica i dettagli COMPLETI dei documenti firmati E dello storico
        const contracts = data.contracts || []
        const history = data.contractsHistory || []
        
        // Unisci tutti i documentId unici (contratti attuali + storico)
        const allDocumentIds = new Set()
        contracts.forEach(c => c.documentId && allDocumentIds.add(c.documentId))
        history.forEach(h => h.documentId && allDocumentIds.add(h.documentId))
        
        if (allDocumentIds.size > 0) {
          const detailsMap = {}
          await Promise.all(
            Array.from(allDocumentIds).map(async (documentId) => {
              try {
                // Usa il nuovo endpoint che recupera documenti anche se archiviati
                const res = await organizationApi.getSignedDocumentContent(documentId)
                if (res.data.data?.document) {
                  detailsMap[documentId] = res.data.data.document
                }
              } catch (err) {
                console.error(`Error loading document ${documentId}:`, err)
                // Fallback: cerca i metadati base nei contratti o nello storico
                const contract = contracts.find(c => c.documentId === documentId)
                const historyItem = history.find(h => h.documentId === documentId)
                detailsMap[documentId] = {
                  title: contract?.documentTitle || historyItem?.documentTitle,
                  status: contract?.documentStatus || 'archived'
                }
              }
            })
          )
          setDocumentDetails(detailsMap)
        }
      } else {
        // Fallback: carica dai vecchi endpoint
        const [pendingRes, contractsRes] = await Promise.all([
          organizationOnboardingApi.getPendingContracts().catch(err => {
            console.error('Error loading pending:', err)
            return { data: { data: { pending: [] } } }
          }),
          organizationApi.getContracts().catch(err => {
            console.error('Error loading contracts:', err)
            return { data: { contracts: [] } }
          })
        ])
        
        setPendingDocs(pendingRes.data.data?.pending || [])
        const contracts = contractsRes.data.contracts || contractsRes.data.data?.contracts || []
        setSignedContracts(contracts)
        setContractsHistory([]) // Non disponibile con vecchi endpoint
        
        // Carica i dettagli dei documenti firmati (usa nuovo endpoint che recupera anche archiviati)
        if (contracts.length > 0) {
          const detailsMap = {}
          await Promise.all(
            contracts.map(async (contract) => {
              if (contract.documentId) {
                try {
                  // Usa il nuovo endpoint che recupera documenti anche se archiviati
                  const res = await organizationApi.getSignedDocumentContent(contract.documentId)
                  if (res.data.data?.document) {
                    detailsMap[contract.documentId] = res.data.data.document
                  }
                } catch (err) {
                  console.error(`Error loading document ${contract.documentId}:`, err)
                  // Fallback: prova con l'endpoint pubblico (solo documenti attivi)
                  try {
                    const fallbackRes = await legalDocumentsApi.getDocument(contract.documentId)
                    if (fallbackRes.data.data?.document) {
                      detailsMap[contract.documentId] = fallbackRes.data.data.document
                    }
                  } catch (fallbackErr) {
                    // Documento non disponibile
                    console.error(`Fallback also failed for document ${contract.documentId}:`, fallbackErr)
                  }
                }
              }
            })
          )
          setDocumentDetails(detailsMap)
        }
      }
      
    } catch (err) {
      console.error('Error loading data:', err)
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  const handleAccept = async (document) => {
    try {
      setAccepting(document._id)
      
      await organizationOnboardingApi.acceptContracts([document._id])
      
      showSuccess(`${TYPE_DISPLAY_NAMES[document.type] || document.title} accettato con successo`)
      
      // Ricarica i dati
      await loadData()
      
    } catch (err) {
      console.error('Error accepting document:', err)
      showError(err.response?.data?.message || 'Errore nell\'accettazione del documento')
    } finally {
      setAccepting(null)
    }
  }
  
  if (loading) {
    return <PageLoader />
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
            Errore nel caricamento
          </h2>
          <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
          <button onClick={loadData} className="btn-primary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Riprova
          </button>
        </div>
      </div>
    )
  }
  
  const hasPending = pendingDocs.length > 0
  const hasSigned = signedContracts.length > 0
  const hasHistory = contractsHistory.length > 0
  
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/organization"
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Documenti Legali
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Gestisci i documenti legali della tua organizzazione
            </p>
          </div>
        </div>
        
        <button
          onClick={loadData}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Aggiorna"
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>
      
      {/* Pending Documents Section */}
      {hasPending && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Documenti da firmare ({pendingDocs.length})
            </h2>
          </div>
          
          <div className="space-y-4">
            {pendingDocs.map(doc => (
              <PendingDocumentCard
                key={doc._id}
                document={doc}
                onAccept={handleAccept}
                accepting={accepting === doc._id}
              />
            ))}
          </div>
        </section>
      )}
      
      {/* Signed Contracts Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Documenti firmati {hasSigned && `(${signedContracts.length})`}
          </h2>
        </div>
        
        {hasSigned ? (
          <div className="space-y-3">
            {signedContracts.map((contract, idx) => (
              <SignedContractRow
                key={contract._id || idx}
                contract={contract}
                documentDetails={documentDetails[contract.documentId]}
              />
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center">
            <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              Nessun documento ancora firmato
            </p>
          </Card>
        )}
      </section>
      
      {/* Contracts History Section */}
      {hasHistory && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Storico firme ({contractsHistory.length})
            </h2>
          </div>
          
          <Card className="overflow-hidden">
            {contractsHistory.map((history, idx) => (
              <HistoryContractRow
                key={history._id || idx}
                history={history}
                documentDetails={documentDetails[history.documentId]}
              />
            ))}
          </Card>
        </section>
      )}
      
      {/* Empty state if nothing */}
      {!hasPending && !hasSigned && !hasHistory && (
        <EmptyState
          icon={FileText}
          title="Nessun documento"
          description="Non ci sono documenti legali da visualizzare al momento."
        />
      )}
      
      {/* Info */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-800 dark:text-blue-200">
              Informazioni sui documenti legali
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              I documenti legali devono essere accettati per poter utilizzare tutti i servizi della piattaforma.
              Quando vengono pubblicati nuovi documenti o aggiornamenti, riceverai una notifica email.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
